<script setup>
  import { ref, onMounted, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import axios from '../../services/ApiService.js';
  import Pagination from '../Pagination.vue';

  const { t, locale } = useI18n();
  const i18nScope = 'components.imprints_list';

  const props = defineProps({
    imprintId: {
      type: Number,
      required: true
    },
    imprintName: {
      type: String,
      required: true
    }
  });

  const emit = defineEmits(['back']);

  const authorsLoading = ref(false);
  const authors = ref([]);
  const currentPage = ref(1);
  const itemsPerPage = ref(20);
  const maxPagesToShow = ref(12);
  const totalItems = ref(0);
  const sortBy = ref('name');
  const sortOrder = ref('asc');
  const previousSortBy = ref('name');
  const previousSortOrder = ref('asc');

  const fetchAuthors = async () => {
    authorsLoading.value = true;

    try {
      let url = `/api/imprints/${props.imprintId}/authors.json?page=${currentPage.value}&per_page=${itemsPerPage.value}`;
      url += `&sort_by=${sortBy.value}&sort_order=${sortOrder.value}`;
      const response = await axios.get(url);
      authors.value = response.data.authors || [];
      totalItems.value = response.data.total || 0;

    } catch (error) {
      console.error('Error fetching authors:', error);
      authors.value = [];
      totalItems.value = 0;
    } finally {
      authorsLoading.value = false;
    }
  };

  const handleSort = (column) => {
    if (sortBy.value === column) {
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
    } else {
      sortBy.value = column;
      sortOrder.value = 'asc';
    }
    
    // Reset to page 1 if sorting changed
    if (sortBy.value !== previousSortBy.value || sortOrder.value !== previousSortOrder.value) {
      currentPage.value = 1;
    }
    
    // Update previous values
    previousSortBy.value = sortBy.value;
    previousSortOrder.value = sortOrder.value;
    
    fetchAuthors();
  };

  const getSortIcon = (column) => {
    if (sortBy.value !== column) return '';
    return sortOrder.value === 'asc' ? '↑' : '↓';
  };

  const handleViewPublications = (author) => {
    window.open(`/recent?author_ids=${author.id}&languages=all`, '_blank');
  };

  const handlePageChange = (page) => {
    currentPage.value = page;
    fetchAuthors();
  };

  const handleBack = () => {
    emit('back');
  };

  onMounted(() => {
    fetchAuthors();
  });
</script>

<template>
  <div class="authors-table">
    <!-- Back button and title -->
    <div class="d-flex align-items-center mb-4">
      <button 
        type="button" 
        class="btn btn-link p-0 me-3"
        @click="handleBack">
        <i class="bi bi-arrow-left"></i>
        {{ t('components.imprints_list.table.back_to_publishers') }}
      </button>
    </div>

    <h3 class="mb-4">{{ t('components.imprints_list.table.authors_for') }} {{ imprintName }}</h3>

    <!-- Loading Spinner -->
    <div v-if="authorsLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ t('components.imprints_list.loading') }}</span>
      </div>
    </div>

    <!-- Table -->
    <div v-else-if="authors.length > 0" class="table-responsive">
      <table class="table table-hover table-striped">
        <thead class="table-light">
          <tr>
            <th scope="col" class="sortable" @click="handleSort('name')">
              {{ t('components.imprints_list.table.author_name') }} {{ getSortIcon('name') }}
            </th>
            <th scope="col" class="sortable text-end" @click="handleSort('titles_count')">
              {{ t('components.imprints_list.table.titles') }} {{ getSortIcon('titles_count') }}
            </th>
            <th scope="col" class="text-center">
              {{ t('components.imprints_list.table.publications') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="author in authors" :key="author.id">
            <td>{{ author.name }}</td>
            <td class="text-end">
              {{ author.titles_count || '-' }}
            </td>
            <td class="text-center">
              <i 
                class="bi bi-box-arrow-up-right action-icon"
                @click="handleViewPublications(author)"
                data-bs-toggle="tooltip" :data-bs-title="t('components.imprints_list.table.browse_author_publications')" :aria-label="t('components.imprints_list.table.browse_author_publications')"
              ></i>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <Pagination
        :current-page="currentPage"
        :total-items="totalItems"
        :items-per-page="itemsPerPage"
        :max-pages-to-show="maxPagesToShow"
        @page-change="handlePageChange"
      />
    </div>

    <!-- No Results -->
    <div v-else class="text-center py-5">
      <p class="text-muted">{{ t('components.imprints_list.no_results') }}</p>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .authors-table {
    position: relative;

    .sortable {
      cursor: pointer;
      user-select: none;
      
      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }
    }

    .table {
      margin-bottom: 20px;
    }

    .btn-link {
      color: var(--primary);
      text-decoration: none;
      font-size: 1rem;

      &:hover {
        text-decoration: underline;
      }
    }

    .action-icon {
      cursor: pointer;
      font-size: 1.2rem;
      color: var(--primary);

      &:hover {
        opacity: 0.8;
      }
    }
  }
</style>
